import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Typography, Paper, CircularProgress, Box, Button } from '@mui/material';
import API from '@utils/axios';
import Layout from '@components/Layout';
import PageError from '@components/PageError';

function UnsubscribePage() {
    const { t } = useTranslation();
    const { key } = useParams();

    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState(null);
    const [data, setData] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await API.get(`/unsubscriptions/${key}`);
                setData(data);
                setConfirmed(data.already_unsubscribed);
            } catch (err) {
                if (err.response?.status === 404) {
                    setErrorCode(err.response.data.code ?? '404');
                } else {
                    setErrorCode('internal_error');
                    console.error('Error:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        if (key) {
            fetchData();
        }
    }, [key, setData]);

    const handleConfirm = async () => {
        setConfirmLoading(true);
        try {
            const response = await API.post(`/unsubscriptions/${key}`);
            if (response.status === 200 && response.data.success) {
                setConfirmed(true);
            } else {
                setErrorCode('confirm_error');
            }
        } catch (err) {
            console.error('Error:', err);
            setErrorCode('confirm_internal_error');
        } finally {
            setConfirmLoading(false);
        }
    };

    return errorCode ? (
        <PageError
            title={t('Page not found')}
            message={t('This link is invalid or expired.')}
            message2={t('Error code: {{code}}', { code: errorCode })}
        />
    ) : (
        loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: '20vh' }} >
                <CircularProgress />
            </Box >
        ) : (
            confirmed ? (
                <Layout customer={data.customer} size="sm" title={t('Unsubscribe confirmed')} >
                    <Paper sx={{ px: 3, py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h2">
                            {t('Unsubscribe confirmed')}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 5 }}>
                            {t('You have been successfully unsubscribed from our communications. You will no longer receive emails from us.')}
                        </Typography>
                    </Paper>
                </Layout >
            ) : (
                <Layout customer={data.customer} size="sm" title={t('Confirm your choice')} >
                    <Paper sx={{ px: 3, py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h2">
                            {t('Confirm your choice')}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 5 }}>
                            {t('You are about to unsubscribe from our communications. Once your unsubscription is confirmed, you will no longer receive emails from us at the following address:')}
                            {' '}<strong>{data.value}</strong>
                        </Typography>
                        <Button
                            onClick={handleConfirm}
                            disabled={confirmLoading}
                            variant="contained"
                            color="primary"
                            size="large"
                        >
                            {t('Unsubscribe')}
                        </Button>
                    </Paper>
                </Layout >
            )
        )
    );
}

export default UnsubscribePage;
